import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { formatEur, formatNumber, formatPct, formatDateTime } from "../lib/format";
import { FlipScoreGauge } from "../components/FlipScoreGauge";
import ConfidenceBadge from "../components/ConfidenceBadge";
import ZoneBadge from "../components/ZoneBadge";
import EmptyState from "../components/EmptyState";
import FinancialPanel from "../components/FinancialPanel";
import {
    ArrowLeft,
    ExternalLink,
    MapPin,
    Bookmark,
    Sparkles,
    AlertOctagon,
    Image as ImageIcon,
    CheckCircle2,
    Circle,
    Waves,
    LineChart,
    Lock,
} from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";

// Fix default marker icons (Leaflet + Webpack quirk)
const DefaultIcon = L.icon({
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const DUE_DILIGENCE = {
    legal: [
        "Eigendomstitel controleren",
        "Kadastrale registratie",
        "Bouwvergunning / legalisatie",
        "Geen claims of hypotheken",
        "Toegang tot openbare weg",
        "VvE / servicekosten (appartement)",
        "Verhuurbeperkingen",
        "Buitenlandse koper toegestaan",
    ],
    technical: [
        "Constructie",
        "Dak",
        "Vocht",
        "Elektra",
        "Leidingen",
        "Airco",
        "Ramen",
        "Terras / balkon",
        "Zwembad (indien aanwezig)",
    ],
    commercial: [
        "Marktwaarde na renovatie",
        "Verkoopbaarheid",
        "Verhuurbaarheid",
        "Concurrentie in dezelfde wijk",
        "Realistische exit-prijs",
    ],
};

const DealDetail = () => {
    const { id } = useParams();
    const [listing, setListing] = useState(null);
    const [financial, setFinancial] = useState(null);
    const [memo, setMemo] = useState(null);
    const [comparables, setComparables] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const l = await api.get(`/listings/${id}`);
            setListing(l.data);
            // financial calc may be blocked (real-data discipline)
            const f = await api.post(`/listings/${id}/financial`).catch(() => null);
            if (f) setFinancial(f.data);
            const m = await api.get(`/listings/${id}/memo`).catch(() => null);
            if (m && m.data) setMemo(m.data);
            const c = await api.get(`/listings/${id}/comparables?k=5`).catch(() => null);
            if (c) setComparables(c.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const onGenerateMemo = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post(`/listings/${id}/memo`);
            setMemo(data);
            toast.success("AI Investment Memo generated");
        } catch (e) {
            const msg = e?.response?.data?.detail || String(e);
            toast.error("Memo failed", { description: msg });
        } finally {
            setGenerating(false);
        }
    };

    const onAddWatchlist = async () => {
        try {
            await api.post("/watchlist", {
                listing_id: id,
                bid_status: "watching",
            });
            toast.success("Added to watchlist");
        } catch (e) {
            toast.error("Could not add to watchlist");
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-72" />
                <Skeleton className="h-80 w-full" />
            </div>
        );
    }

    if (!listing) {
        return (
            <EmptyState
                title="Listing niet gevonden"
                description="Mogelijk is deze listing verwijderd of nog niet gesynchroniseerd door een succesvolle scan."
            />
        );
    }

    const blocked = financial?.blocked_reason;

    return (
        <div className="space-y-6" data-testid="deal-detail-page">
            <div>
                <Button asChild variant="ghost" size="sm" className="gap-1 -ml-2" data-testid="back-to-deals">
                    <Link to="/deals">
                        <ArrowLeft className="w-4 h-4" /> Back to Deal Finder
                    </Link>
                </Button>
            </div>

            {/* Header */}
            <Card className="p-6">
                <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-start">
                    <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                            <ZoneBadge zone={listing.zone_class} testid="detail-zone" />
                            <ConfidenceBadge
                                status={listing.provenance?.verification_status}
                                testid="detail-confidence"
                            />
                            <Badge variant="outline" className="text-[10px] uppercase">
                                {listing.property_type || "—"}
                            </Badge>
                            <Badge variant="outline" className="text-[10px] uppercase">
                                {listing.status}
                            </Badge>
                        </div>
                        <h1 className="font-grotesk text-2xl md:text-3xl font-semibold">
                            {listing.title || "Untitled listing"}
                        </h1>
                        <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
                            <MapPin className="w-4 h-4" />
                            <span>
                                {listing.municipality || "—"}
                                {listing.neighbourhood ? ` · ${listing.neighbourhood}` : ""}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-6 pt-2">
                            <Stat label="Asking price" value={formatEur(listing.asking_price_eur)} big />
                            <Stat
                                label="€ / m²"
                                value={listing.price_per_sqm_eur ? formatEur(listing.price_per_sqm_eur) : "—"}
                            />
                            <Stat
                                label="Living area"
                                value={listing.living_area_sqm ? `${formatNumber(listing.living_area_sqm)} m²` : "—"}
                            />
                            <Stat label="Bedrooms" value={listing.bedrooms ?? "—"} />
                            <Stat label="Bathrooms" value={listing.bathrooms ?? "—"} />
                            <Stat
                                label="Sea view"
                                value={listing.sea_view === "unknown" ? "—" : listing.sea_view}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col items-center gap-3">
                        <FlipScoreGauge
                            score={listing.flip_score}
                            label={listing.flip_label}
                            size={160}
                            testid="detail-flip-score"
                        />
                        <div className="text-xs font-medium uppercase tracking-[0.16em] text-center text-muted-foreground max-w-[180px]">
                            {listing.flip_label || "Awaiting real data"}
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={onAddWatchlist} data-testid="watchlist-add">
                                <Bookmark className="w-4 h-4 mr-1.5" /> Watchlist
                            </Button>
                            <Button asChild variant="ghost" size="sm" data-testid="open-source">
                                <a href={listing.original_url} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="w-4 h-4 mr-1.5" />
                                    Source
                                </a>
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Tabs */}
            <Tabs defaultValue="overview" className="space-y-4">
                <TabsList data-testid="detail-tabs">
                    <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                    <TabsTrigger value="comparables" data-testid="tab-comparables">Comparables</TabsTrigger>
                    <TabsTrigger value="financial" data-testid="tab-financial">Financial analysis</TabsTrigger>
                    <TabsTrigger value="memo" data-testid="tab-memo">AI Investment Memo</TabsTrigger>
                    <TabsTrigger value="vision" data-testid="tab-vision">AI Vision</TabsTrigger>
                    <TabsTrigger value="due-diligence" data-testid="tab-dd">Due diligence</TabsTrigger>
                    <TabsTrigger value="provenance" data-testid="tab-provenance">Provenance</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                    <div className="grid lg:grid-cols-[1fr_1fr] gap-4">
                        <Card className="p-5">
                            <h3 className="font-grotesk font-semibold text-sm mb-3">Photos</h3>
                            {listing.photo_urls?.length ? (
                                <div className="grid grid-cols-2 gap-2">
                                    {listing.photo_urls.slice(0, 6).map((u, i) => (
                                        <img
                                            key={`${u}-${i}`}
                                            src={u}
                                            alt=""
                                            className="aspect-[4/3] rounded-md object-cover bg-muted"
                                        />
                                    ))}
                                </div>
                            ) : (
                                <EmptyState
                                    icon={ImageIcon}
                                    title="Geen foto's beschikbaar"
                                    description="Foto-URLs werden niet door de scraper meegegeven."
                                />
                            )}
                        </Card>
                        <Card className="p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-grotesk font-semibold text-sm">Location</h3>
                                {listing.geocoding_status === "approximate" && (
                                    <Badge variant="outline" className="text-[10px] uppercase border-accent/50 text-accent">
                                        Approximate
                                    </Badge>
                                )}
                                {listing.geocoding_status === "not_geocoded" && (
                                    <Badge variant="outline" className="text-[10px] uppercase text-muted-foreground">
                                        Not geocoded
                                    </Badge>
                                )}
                            </div>
                            {listing.latitude && listing.longitude ? (
                                <div className="h-80 rounded-md overflow-hidden">
                                    <MapContainer
                                        center={[listing.latitude, listing.longitude]}
                                        zoom={listing.geocoding_status === "approximate" ? 11 : 14}
                                        className="h-full w-full"
                                    >
                                        <TileLayer
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            attribution='© OpenStreetMap contributors'
                                        />
                                        <Marker position={[listing.latitude, listing.longitude]}>
                                            <Popup>
                                                {listing.title || listing.municipality}
                                                {listing.geocoding_status === "approximate" && (
                                                    <>
                                                        <br />
                                                        <em>Approximate — geocoded from area name only.</em>
                                                    </>
                                                )}
                                            </Popup>
                                        </Marker>
                                    </MapContainer>
                                </div>
                            ) : (
                                <EmptyState
                                    icon={MapPin}
                                    title="Location not geocoded"
                                    description="No coordinates available from the source listing, and Nominatim could not resolve the area name. Run a geocoding pass from the Sources page or geocode manually."
                                />
                            )}
                            <Separator className="my-4" />
                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <KV label="Afstand zee" value={listing.distance_to_sea_m ? `${formatNumber(listing.distance_to_sea_m)} m` : "—"} />
                                <KV label="Afstand luchthaven" value={listing.distance_to_airport_km ? `${formatNumber(listing.distance_to_airport_km)} km` : "—"} />
                                <KV label="Afstand marina" value={listing.distance_to_marina_km ? `${formatNumber(listing.distance_to_marina_km)} km` : "—"} />
                                <KV label="Afstand oude stad" value={listing.distance_to_old_town_km ? `${formatNumber(listing.distance_to_old_town_km)} km` : "—"} />
                                <KV label="Plot" value={listing.plot_area_sqm ? `${formatNumber(listing.plot_area_sqm)} m²` : "—"} />
                                <KV label="Year built" value={listing.year_built ?? "—"} />
                            </div>
                        </Card>
                    </div>
                    {listing.description && (
                        <Card className="p-5">
                            <h3 className="font-grotesk font-semibold text-sm mb-3">Description</h3>
                            <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-line">
                                {listing.description}
                            </p>
                        </Card>
                    )}
                    {listing.score_breakdown && (
                        <Card className="p-5">
                            <h3 className="font-grotesk font-semibold text-sm mb-4">
                                Flip score breakdown
                            </h3>
                            <div className="space-y-3">
                                {Object.entries(listing.score_breakdown).map(([k, v]) => (
                                    <div key={k}>
                                        <div className="flex justify-between text-xs mb-1">
                                            <span className="uppercase tracking-wider text-muted-foreground">
                                                {k.replace(/_/g, " ")}
                                            </span>
                                            <span className="font-medium tabular-nums">{formatNumber(v, 1)}</span>
                                        </div>
                                        <div className="h-1.5 bg-muted rounded overflow-hidden">
                                            <div
                                                className="h-full bg-accent"
                                                style={{ width: `${Math.min(100, Math.max(0, v * 5))}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                </TabsContent>

                <TabsContent value="comparables" className="space-y-4">
                    <ComparablesPanel comparables={comparables} subject={listing} />
                </TabsContent>

                <TabsContent value="financial" className="space-y-4">
                    {blocked ? (
                        <EmptyState
                            icon={AlertOctagon}
                            title="Calculation blocked"
                            description={blocked}
                            testid="financial-blocked"
                        />
                    ) : financial ? (
                        <FinancialPanel financial={financial} comparables={comparables} />
                    ) : (
                        <EmptyState
                            title="No financial analysis available"
                            description="Add renovation + exit assumptions in Settings, or run a scan to populate missing fields."
                        />
                    )}
                </TabsContent>

                <TabsContent value="memo" className="space-y-4">
                    {!memo ? (
                        <Card className="p-8 text-center space-y-4" data-testid="memo-empty">
                            <h3 className="font-grotesk font-semibold text-lg">
                                Geen AI memo gegenereerd
                            </h3>
                            <p className="text-sm text-muted-foreground max-w-md mx-auto">
                                De AI Investment Analyst (Claude Sonnet 4.5) genereert een investeringsmemo op basis van
                                echte listingdata. Geen verzonnen feiten.
                            </p>
                            <Button onClick={onGenerateMemo} disabled={generating} data-testid="generate-memo">
                                <Sparkles className="w-4 h-4 mr-1.5" />
                                {generating ? "Generating…" : "Generate AI memo"}
                            </Button>
                        </Card>
                    ) : (
                        <Card className="p-6 space-y-5" data-testid="memo-card">
                            <div className="flex justify-between items-start gap-4 flex-wrap">
                                <div>
                                    <Badge variant="outline" className="mb-2 text-[10px] uppercase">
                                        {memo.confidence_label}
                                    </Badge>
                                    <h2 className="font-grotesk text-xl font-semibold">{memo.title}</h2>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Generated {formatDateTime(memo.generated_at)} · {memo.model}
                                    </p>
                                </div>
                                <Button variant="outline" size="sm" onClick={onGenerateMemo} disabled={generating}>
                                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Regenerate
                                </Button>
                            </div>
                            <MemoSection title="Executive view" body={memo.executive_view} />
                            <MemoSection title="Why it is interesting" body={memo.why_interesting} />
                            <MemoSection title="Risks" body={memo.risks} />
                            <MemoSection title="Renovation strategy" body={memo.renovation_strategy} />
                            <MemoSection title="Buyer psychology" body={memo.buyer_psychology} />
                            <MemoSection title="Negotiation strategy" body={memo.negotiation_strategy} />
                            <Separator />
                            <div className="grid md:grid-cols-4 gap-4">
                                <BidCell label="Opening bid" value={memo.opening_bid_eur ? formatEur(memo.opening_bid_eur) : "—"} />
                                <BidCell label="Maximum bid" value={memo.maximum_bid_eur ? formatEur(memo.maximum_bid_eur) : "—"} />
                                <BidCell label="Walk away" value={memo.walk_away_eur ? formatEur(memo.walk_away_eur) : "—"} />
                                <BidCell label="Final advice" value={memo.final_recommendation} highlight />
                            </div>
                            {memo.based_on_inputs?.length > 0 && (
                                <div className="text-[11px] text-muted-foreground">
                                    Based on inputs: {memo.based_on_inputs.join(", ")}
                                </div>
                            )}
                        </Card>
                    )}
                </TabsContent>

                <TabsContent value="vision" className="space-y-4">
                    <VisionPanel
                        listing={listing}
                        onUpdated={(updated) => setListing(updated)}
                    />
                </TabsContent>

                <TabsContent value="due-diligence" className="space-y-4">
                    <div className="grid md:grid-cols-3 gap-4">
                        {Object.entries(DUE_DILIGENCE).map(([cat, items]) => (
                            <Card key={cat} className="p-5">
                                <h3 className="font-grotesk font-semibold text-sm uppercase tracking-wider mb-3">
                                    {cat}
                                </h3>
                                <ul className="space-y-2.5 text-sm">
                                    {items.map((item) => (
                                        <li key={item} className="flex items-start gap-2 text-foreground/90">
                                            <Circle className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                                            <span className="leading-snug">{item}</span>
                                            <Badge
                                                variant="outline"
                                                className="ml-auto text-[10px] py-0 px-1.5 text-muted-foreground"
                                            >
                                                Unknown
                                            </Badge>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Statusveranderingen zijn read-only in de MVP. Wire-up volgt zodra echte
                        due-diligence inputs (bv. notaris of kadaster) zijn aangesloten.
                    </p>
                </TabsContent>

                <TabsContent value="provenance">
                    <Card className="p-6 space-y-4" data-testid="provenance-card">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <KV label="Source" value={listing.provenance?.source_name} />
                            <KV label="Source URL" value={
                                <a className="underline text-accent" href={listing.provenance?.source_url} target="_blank" rel="noopener noreferrer">
                                    {listing.provenance?.source_url}
                                </a>
                            } />
                            <KV label="Retrieved at" value={formatDateTime(listing.provenance?.retrieved_at)} />
                            <KV label="Last checked" value={formatDateTime(listing.provenance?.last_checked_at)} />
                            <KV label="Confidence score" value={formatNumber(listing.provenance?.confidence_score, 2)} />
                            <KV label="Verification" value={<ConfidenceBadge status={listing.provenance?.verification_status} />} />
                        </div>
                        {listing.provenance?.notes && (
                            <p className="text-xs text-muted-foreground">{listing.provenance.notes}</p>
                        )}
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

const ComparablesPanel = ({ comparables, subject }) => {
    if (!comparables) {
        return (
            <Card className="p-8 text-center" data-testid="comparables-loading">
                <Skeleton className="h-6 w-1/2 mx-auto mb-3" />
                <Skeleton className="h-32 w-full" />
            </Card>
        );
    }

    if (comparables.insufficient) {
        return (
            <Card className="p-6 border-amber-500/30 bg-amber-500/5" data-testid="comparables-insufficient">
                <div className="flex items-start gap-3 mb-3">
                    <Lock className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <div>
                        <h3 className="font-grotesk font-semibold text-sm text-amber-300">
                            Insufficient comparable set
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            {comparables.blocked_reason}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                            Subject: <strong>{comparables.subject_municipality || "—"}</strong>{" "}
                            {comparables.subject_neighbourhood ? `· ${comparables.subject_neighbourhood}` : ""}{" "}
                            · category <strong>{comparables.subject_category}</strong> ·{" "}
                            pool of {comparables.candidate_pool_size} same-municipality listings examined,
                            {comparables.selected_count} matched all criteria
                            (same category · area within ±35% · bedrooms within ±1 · last seen ≤90d).
                        </p>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <div className="space-y-4" data-testid="comparables-panel">
            <Card className="p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h3 className="font-grotesk font-semibold text-base flex items-center gap-2">
                            <LineChart className="w-4 h-4 text-accent" />
                            Comparable engine
                        </h3>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                            {comparables.selected_count} comps from real {comparables.subject_category} inventory in {comparables.subject_municipality}
                            · same category · area ±35% · beds ±1 · last seen ≤90d
                        </p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                        Confidence {Math.round((comparables.confidence || 0) * 100)}%
                    </Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KeyVal label="Subject €/m²" value={comparables.subject_price_per_sqm ? `€${formatNumber(comparables.subject_price_per_sqm, 0)}` : "—"} />
                    <KeyVal label="Comp median €/m²" value={comparables.median_comp_price_per_sqm ? `€${formatNumber(comparables.median_comp_price_per_sqm, 0)}` : "—"} />
                    <KeyVal
                        label="Discount to comps"
                        value={comparables.discount_to_comps_pct != null ? (
                            <span className={comparables.discount_to_comps_pct > 0 ? "text-emerald-400" : "text-rose-400"}>
                                {comparables.discount_to_comps_pct > 0 ? "−" : "+"}
                                {Math.abs(comparables.discount_to_comps_pct).toFixed(1)}%
                            </span>
                        ) : "—"}
                    />
                    <KeyVal label="Pool examined" value={`${comparables.candidate_pool_size} listings`} />
                </div>
            </Card>

            <Card className="p-0 overflow-hidden">
                <div className="px-5 py-3 border-b border-border/60">
                    <h4 className="text-sm font-medium">Top {comparables.selected_count} comparables</h4>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border/60 text-xs uppercase tracking-wider text-muted-foreground">
                                <th className="py-2 px-3 text-left">Listing</th>
                                <th className="py-2 px-3 text-right">Price</th>
                                <th className="py-2 px-3 text-right">m²</th>
                                <th className="py-2 px-3 text-right">€/m²</th>
                                <th className="py-2 px-3 text-right">Beds</th>
                                <th className="py-2 px-3 text-left">Match</th>
                                <th className="py-2 px-3 text-left">Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            {comparables.comparables.map((c) => (
                                <tr key={c.id} className="border-b border-border/30 hover:bg-muted/30" data-testid={`comp-row-${c.id}`}>
                                    <td className="py-2 px-3">
                                        <Link to={`/deals/${c.id}`} className="block hover:text-accent">
                                            <div className="font-medium line-clamp-1 max-w-[280px]">{c.title || "Untitled"}</div>
                                            <div className="text-[10px] text-muted-foreground">
                                                {[c.municipality, c.neighbourhood].filter(Boolean).join(" · ")}
                                            </div>
                                        </Link>
                                    </td>
                                    <td className="py-2 px-3 text-right">{c.asking_price_eur ? formatEur(c.asking_price_eur) : "—"}</td>
                                    <td className="py-2 px-3 text-right">{c.living_area_sqm ? formatNumber(c.living_area_sqm, 0) : (c.plot_area_sqm ? formatNumber(c.plot_area_sqm, 0) : "—")}</td>
                                    <td className="py-2 px-3 text-right">{c.price_per_sqm_eur ? `€${formatNumber(c.price_per_sqm_eur, 0)}` : "—"}</td>
                                    <td className="py-2 px-3 text-right">{c.bedrooms ?? "—"}</td>
                                    <td className="py-2 px-3">
                                        <div className="flex flex-wrap gap-1 text-[10px]">
                                            {c.match_breakdown?.same_neighbourhood === true && <Badge variant="outline" className="border-emerald-500/30 text-emerald-400">same neigh</Badge>}
                                            {c.match_breakdown?.sea_view_match === true && <Badge variant="outline" className="border-blue-500/30 text-blue-300">sea-view match</Badge>}
                                            {c.match_breakdown?.area_distance != null && (
                                                <span className="text-muted-foreground">
                                                    Δ area {Math.round(c.match_breakdown.area_distance * 100)}%
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-2 px-3 text-[11px] text-muted-foreground">{c.provenance_source || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            {subject?.sea_view && subject.sea_view !== "unknown" && (
                <Card className="p-4">
                    <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                        <Waves className="w-4 h-4 text-accent" />
                        Sea-view evidence
                    </h4>
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                        <Badge variant="outline" className="border-blue-500/30 text-blue-300 text-[10px] uppercase">
                            {subject.sea_view}
                        </Badge>
                        {subject.sea_view_confidence_score != null && (
                            <span className="text-muted-foreground">
                                confidence {subject.sea_view_confidence_score}/100
                            </span>
                        )}
                        {(subject.sea_view_signals || []).length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {subject.sea_view_signals.slice(0, 6).map((s, i) => (
                                    <Badge key={`${s.phrase}-${s.where}-${i}`} variant="secondary" className="text-[10px]">
                                        {s.phrase} <span className="opacity-60 ml-1">({s.where} +{s.weight})</span>
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                </Card>
            )}
        </div>
    );
};

const KeyVal = ({ label, value }) => (
    <div>
        <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="text-sm text-foreground mt-0.5">{value}</div>
    </div>
);


const Stat = ({ label, value, big }) => (
    <div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
        <div className={big ? "kpi-value text-4xl" : "font-grotesk font-semibold text-lg"}>{value}</div>
    </div>
);

const KV = ({ label, value }) => (
    <div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
        <div className="text-sm font-medium break-words">{value || "—"}</div>
    </div>
);

const BidCell = ({ label, value, hint, highlight }) => (
    <div className={`p-4 rounded-md ${highlight ? "bg-primary text-primary-foreground" : "bg-muted/60"}`}>
        <div className={`text-[10px] uppercase tracking-[0.18em] ${highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
            {label}
        </div>
        <div className="font-grotesk font-semibold text-base mt-1">{value}</div>
        {hint && (
            <div className={`text-[11px] mt-1 ${highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                {hint}
            </div>
        )}
    </div>
);

const MemoSection = ({ title, body }) => (
    <section>
        <h3 className="font-grotesk font-semibold text-xs uppercase tracking-[0.18em] text-accent mb-2">
            {title}
        </h3>
        <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-line">
            {body}
        </p>
    </section>
);

const VISION_KEYS = [
    ["sea_view", "Sea view"],
    ["sea_view_quality", "View quality"],
    ["renovation_status", "Renovation status"],
    ["interior_datedness", "Interior"],
    ["luxury_potential", "Luxury potential"],
    ["terrace_balcony_quality", "Terrace / balcony"],
    ["view_quality", "Overall view"],
];

const VisionPanel = ({ listing, onUpdated }) => {
    const [running, setRunning] = useState(false);
    const a = listing.vision_assessment;

    const run = async () => {
        setRunning(true);
        try {
            const { data } = await api.post(`/listings/${listing.id}/vision`);
            onUpdated({ ...listing, vision_assessment: data, vision_generated_at: new Date().toISOString() });
            toast.success("Photo assessment complete");
        } catch (e) {
            toast.error("Vision assessment failed", {
                description: e?.response?.data?.detail || String(e),
            });
        } finally {
            setRunning(false);
        }
    };

    if (!a) {
        return (
            <Card className="p-8 text-center space-y-4" data-testid="vision-empty">
                <h3 className="font-grotesk font-semibold text-lg">No photo assessment yet</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Claude inspects up to 4 listing photos and reports sea view, renovation status,
                    interior datedness, luxury potential, terrace quality, view quality and any red
                    flags (damp, dark rooms, cracks). Always labelled
                    <em> "AI image assessment — needs human verification"</em>.
                </p>
                <Button
                    onClick={run}
                    disabled={running || !listing.photo_urls?.length}
                    data-testid="run-vision"
                >
                    <Sparkles className="w-4 h-4 mr-1.5" />
                    {running ? "Analysing…" : "Run AI photo assessment"}
                </Button>
                {!listing.photo_urls?.length && (
                    <p className="text-xs text-destructive">
                        No photo URLs on this listing — vision is blocked.
                    </p>
                )}
            </Card>
        );
    }

    return (
        <Card className="p-6 space-y-5" data-testid="vision-card">
            <div className="flex justify-between items-start gap-4 flex-wrap">
                <div>
                    <Badge variant="outline" className="mb-2 text-[10px] uppercase border-accent/60 text-accent">
                        {a.label || "AI image assessment — needs human verification"}
                    </Badge>
                    <h2 className="font-grotesk text-xl font-semibold">Photo assessment</h2>
                    <p className="text-xs text-muted-foreground mt-1">
                        Confidence: {a.confidence || "—"} · {a.photo_count_used || 0} photos analysed
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={run} disabled={running}>
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Re-analyse
                </Button>
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {VISION_KEYS.map(([k, label]) => (
                    <div key={k} className="bg-muted/40 rounded-md p-3">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                            {label}
                        </div>
                        <div className="font-grotesk font-medium text-sm mt-1 capitalize">
                            {(a[k] || "not_visible").toString().replace(/_/g, " ")}
                        </div>
                    </div>
                ))}
            </div>

            {a.red_flags?.length > 0 && (
                <div>
                    <h4 className="text-[11px] uppercase tracking-wider text-destructive mb-2">
                        Red flags
                    </h4>
                    <div className="flex flex-wrap gap-2">
                        {a.red_flags.map((rf, i) => (
                            <Badge key={`rf-${rf}-${i}`} variant="outline" className="border-destructive/50 text-destructive">
                                {rf}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}

            {a.summary && (
                <div>
                    <h4 className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                        Summary
                    </h4>
                    <p className="text-sm leading-relaxed text-foreground/90">{a.summary}</p>
                </div>
            )}

            {a.evidence?.length > 0 && (
                <div>
                    <h4 className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                        Observations
                    </h4>
                    <ul className="space-y-1.5 text-sm text-foreground/80">
                        {a.evidence.map((ev, i) => (
                            <li key={`ev-${ev.photo_index ?? "x"}-${i}`} className="flex gap-2">
                                <span className="text-muted-foreground tabular-nums">
                                    #{ev.photo_index ?? "?"}
                                </span>
                                <span>{ev.observation}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Card>
    );
};

export default DealDetail;
